import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarBlank, PaperPlaneTilt, Clock, ClockCounterClockwise } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const DAYS_OF_WEEK = [
  { value: '0', label: 'Monday' },
  { value: '1', label: 'Tuesday' },
  { value: '2', label: 'Wednesday' },
  { value: '3', label: 'Thursday' },
  { value: '4', label: 'Friday' },
  { value: '5', label: 'Saturday' },
  { value: '6', label: 'Sunday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${i.toString().padStart(2, '0')}:00 UTC${i >= 3 && i <= 18 ? ` (${((i + 5) % 24 || 12) > 12 ? ((i + 5) % 24) - 12 : (i + 5) % 24 || 12}:30 ${i + 5 >= 12 && i + 5 < 24 ? 'PM' : 'AM'} IST)` : ''}`,
}));

const ScheduledReports = () => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchSchedule = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/reports/schedule`, { withCredentials: true });
      setSchedule(data);
    } catch (err) { console.error(err.message);
      toast.error('Failed to load report schedule');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/reports/history?limit=5`, { withCredentials: true });
      setHistory(data);
    } catch (err) { console.error(err.message); }
  }, []);

  useEffect(() => { fetchSchedule(); fetchHistory(); }, [fetchSchedule, fetchHistory]);

  const updateField = (field, value) => {
    setSchedule(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/reports/schedule`, schedule, { withCredentials: true });
      toast.success('Report schedule saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/reports/send-now`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  if (loading || !schedule) return <div className="text-sm text-muted-foreground py-4">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="scheduled-reports-settings">
      <div className="flex items-center gap-2 mb-1">
        <CalendarBlank size={18} weight="bold" className="text-primary" />
        <Label className="text-sm font-semibold">Scheduled Auto-Reports</Label>
      </div>
      <p className="text-xs text-muted-foreground -mt-3">
        Receive comprehensive PDF summaries of Sales, Purchases, Expenses and Profit delivered to your email automatically.
      </p>

      {/* Enable toggle */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">Enable Auto-Reports</div>
          <div className="text-xs text-muted-foreground">Schedule recurring email reports</div>
        </div>
        <Switch
          checked={schedule.enabled}
          onCheckedChange={(v) => updateField('enabled', v)}
          data-testid="auto-report-toggle"
        />
      </div>

      {schedule.enabled && (
        <div className="space-y-4 pl-0.5">
          {/* Frequency */}
          <div className="space-y-1.5">
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Frequency</Label>
            <Select value={schedule.frequency} onValueChange={(v) => updateField('frequency', v)}>
              <SelectTrigger className="rounded-sm" data-testid="report-frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Day selector (conditional) */}
          {schedule.frequency === 'weekly' && (
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Day of Week</Label>
              <Select value={String(schedule.day_of_week)} onValueChange={(v) => updateField('day_of_week', parseInt(v))}>
                <SelectTrigger className="rounded-sm" data-testid="report-day-of-week-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OF_WEEK.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {schedule.frequency === 'monthly' && (
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Day of Month</Label>
              <Select value={String(schedule.day_of_month)} onValueChange={(v) => updateField('day_of_month', parseInt(v))}>
                <SelectTrigger className="rounded-sm" data-testid="report-day-of-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Time */}
          <div className="space-y-1.5">
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground flex items-center gap-1">
              <Clock size={12} weight="bold" /> Time
            </Label>
            <Select value={String(schedule.hour_utc)} onValueChange={(v) => updateField('hour_utc', parseInt(v))}>
              <SelectTrigger className="rounded-sm" data-testid="report-hour-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map(h => (
                  <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Report Content */}
          <div className="space-y-3">
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Report Contents</Label>
            {[
              { key: 'include_sales', label: 'Sales & Revenue', desc: 'Revenue, clients, MoM growth' },
              { key: 'include_purchases', label: 'Purchases', desc: 'Supplier spend, top suppliers' },
              { key: 'include_expenses', label: 'Expenses', desc: 'Category breakdown, recurring vs one-time' },
              { key: 'include_profit', label: 'Profit & Loss', desc: 'Net profit, margin, monthly P&L' },
              { key: 'include_ai_summary', label: 'AI Executive Summary', desc: 'AI-generated business insights' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <div className="text-sm">{label}</div>
                  <div className="text-[11px] text-muted-foreground">{desc}</div>
                </div>
                <Switch
                  checked={schedule[key]}
                  onCheckedChange={(v) => updateField(key, v)}
                  data-testid={`report-content-${key}`}
                />
              </div>
            ))}
          </div>

          {/* Schedule Summary */}
          <div className="p-3 bg-muted/50 border border-border rounded-sm">
            <div className="text-xs text-muted-foreground">
              {schedule.frequency === 'daily' && `Reports will be sent daily at ${String(schedule.hour_utc).padStart(2, '0')}:00 UTC`}
              {schedule.frequency === 'weekly' && `Reports will be sent every ${DAYS_OF_WEEK[schedule.day_of_week]?.label || 'Monday'} at ${String(schedule.hour_utc).padStart(2, '0')}:00 UTC`}
              {schedule.frequency === 'monthly' && `Reports will be sent on the ${schedule.day_of_month}${['st','nd','rd'][schedule.day_of_month-1] || 'th'} of each month at ${String(schedule.hour_utc).padStart(2, '0')}:00 UTC`}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 rounded-sm"
          data-testid="save-report-schedule-btn"
        >
          {saving ? 'Saving...' : 'Save Schedule'}
        </Button>
        <Button
          variant="outline"
          onClick={handleSendNow}
          disabled={sending}
          className="rounded-sm"
          data-testid="send-report-now-btn"
        >
          <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
          {sending ? 'Sending...' : 'Send Now'}
        </Button>
      </div>

      {/* Recent Report History */}
      {history.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <ClockCounterClockwise size={14} weight="bold" className="text-muted-foreground" />
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Recent Reports</Label>
          </div>
          <div className="space-y-1">
            {history.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs text-muted-foreground py-1.5 border-b border-border last:border-0">
                <span>{r.sent_at?.slice(0, 10)} — {r.recipient}</span>
                <span className="font-medium text-foreground">₹{r.revenue?.toLocaleString()} rev / ₹{r.net_profit?.toLocaleString()} profit</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledReports;
